<template>
  <div class="register-page">
    <div class="register-container">
      <div class="register-card">
        <div class="register-header">
          <button @click="goBack" class="back-button" :title="$t('register.backButtonTitle')">
            <i class="el-icon-arrow-left"></i>
          </button>
          <img class="register-logo" :src="$brand.logo" :alt="$t('brand.logoAlt')" />
          <h1 class="register-title">{{ $t('register.title') }}</h1>
          <p class="register-subtitle">{{ $t('register.subtitle') }}</p>
        </div>
        
        <el-form 
          ref="registerForm"
          :model="form" 
          :rules="rules" 
          label-position="top"
          class="register-form"
        >
          <!-- 组织信息 -->
          <div class="form-section">
            <h2 class="section-title">{{ $t('register.orgInfoSection') }}</h2>
            <el-row :gutter="windowWidth > 576 ? 22 : 12">
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.nameLabel')" prop="organization.name">
                  <el-input v-model="form.organization.name" :placeholder="$t('register.namePlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.legalNameLabel')" prop="organization.legalName">
                  <el-input v-model="form.organization.legalName" :placeholder="$t('register.legalNamePlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24">
                <el-form-item :label="$t('register.industryLabel')" prop="organization.industries">
                  <el-select v-model="form.organization.industries" multiple :placeholder="$t('register.industryPlaceholder')" style="width: 100%">
                    <el-option v-for="item in industryOptions" :key="item.value" :label="item.label" :value="item.value" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.regTypeLabel')" prop="organization.registration.registrationType">
                  <el-select ref="registrationTypeSelect" v-model="form.organization.registration.registrationType" :placeholder="$t('register.regTypePlaceholder')" :loading="registrationTypesLoading" style="width: 100%">
                    <el-option v-for="item in registrationTypeOptions" :key="item.value" :label="item.label" :value="item.value">
                      <div class="registration-type-option">
                        <span class="registration-type-option__label">{{ item.label }}</span>
                        <i
                          class="el-icon-info registration-type-option__info"
                          @click.stop.prevent="toggleRegistrationTypeInfo(item, $event)"
                          @mousedown.stop.prevent
                        ></i>
                      </div>
                    </el-option>
                    <div slot="empty" class="registration-type-empty">
                      <template v-if="registrationTypesLoading">
                        <i class="el-icon-loading registration-type-empty__icon"></i>
                        <span>{{ $t('register.registrationTypesLoading') }}</span>
                      </template>
                      <span v-else>{{ $t('register.registrationTypesEmpty') }}</span>
                    </div>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                 <el-form-item prop="organization.registration.registrationNumber">
                  <template slot="label">
                    <span class="registration-number-label">
                      {{ registrationNumberLabel }}
                      <i
                        v-if="registrationNumberDescription"
                        class="el-icon-info registration-number-info"
                        :class="{ 'is-active': activePopupKey === 'registrationNumber' }"
                        @click.stop.prevent="toggleRegistrationNumberInfo($event)"
                        @mousedown.stop.prevent
                      ></i>
                    </span>
                  </template>
                  <el-input v-model="form.organization.registration.registrationNumber" :placeholder="registrationNumberExample || $t('register.regNumPlaceholder')" />
                </el-form-item>
              </el-col>
               <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.taxIdLabel')" prop="organization.taxIdentificationNumber">
                  <el-input v-model="form.organization.taxIdentificationNumber" :placeholder="$t('register.taxIdPlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.legalRepLastNameLabel')" prop="organization.legalRepresentative.lastName">
                  <el-input v-model="form.organization.legalRepresentative.lastName" :placeholder="$t('register.legalRepLastNamePlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.legalRepFirstNameLabel')" prop="organization.legalRepresentative.firstName">
                  <el-input v-model="form.organization.legalRepresentative.firstName" :placeholder="$t('register.legalRepFirstNamePlaceholder')" />
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 地址与联系方式 -->
          <div class="form-section">
            <h2 class="section-title">{{ $t('register.addressSection') }}</h2>
            <el-row :gutter="windowWidth > 576 ? 22 : 12">
               <el-col :xs="24">
                <el-form-item :label="$t('register.addressLine1Label')" prop="organization.address.addressLine1">
                  <el-input v-model="form.organization.address.addressLine1" :placeholder="$t('register.addressLine1Placeholder')"/>
                </el-form-item>
              </el-col>
              <el-col :xs="24">
                <el-form-item :label="$t('register.addressLine2Label')" prop="organization.address.addressLine2">
                  <el-input v-model="form.organization.address.addressLine2" :placeholder="$t('register.addressLine2Placeholder')"/>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.countryLabel')" prop="organization.address.countryOrRegion">
                  <el-select v-model="form.organization.address.countryOrRegion" filterable :placeholder="$t('register.countryPlaceholder')" style="width: 100%">
                    <el-option v-for="item in countryOptions" :key="item.code" :label="item[$i18n.locale] || item.en" :value="item.code"/>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.stateLabel')" prop="organization.address.stateOrProvince">
                  <el-input v-model="form.organization.address.stateOrProvince" :placeholder="$t('register.statePlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.cityLabel')" prop="organization.address.city">
                  <el-input v-model="form.organization.address.city" :placeholder="$t('register.cityPlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.postalCodeLabel')" prop="organization.address.postalCode">
                  <el-input v-model="form.organization.address.postalCode" :placeholder="$t('register.postalCodePlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.contactEmailLabel')" prop="organization.contactEmail">
                  <el-input v-model="form.organization.contactEmail" :placeholder="$t('register.contactEmailPlaceholder')">
                    <i slot="prefix" class="el-icon-message"></i>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.phoneLabel')" prop="organization.phoneNumber">
                  <el-input v-model="form.organization.phoneNumber" :placeholder="$t('register.phonePlaceholder')">
                    <el-select v-model="phoneCountryCode" slot="prepend" style="width: 85px">
                      <el-option label="+86" value="86"></el-option>
                      <el-option label="+49" value="49"></el-option>
                      <el-option label="+1" value="1"></el-option>
                    </el-select>
                  </el-input>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 管理员信息 -->
          <div class="form-section admin-section">
            <h2 class="section-title">{{ $t('register.adminSection') }}</h2>
             <el-row :gutter="windowWidth > 576 ? 22 : 12">
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.lastNameLabel')" prop="administrator.fullName.lastName">
                  <el-input v-model="form.administrator.fullName.lastName" :placeholder="$t('register.lastNamePlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.firstNameLabel')" prop="administrator.fullName.firstName">
                  <el-input v-model="form.administrator.fullName.firstName" :placeholder="$t('register.firstNamePlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.adminEmailLabel')" prop="administrator.email">
                  <el-input v-model="form.administrator.email" :placeholder="$t('register.adminEmailPlaceholder')">
                     <i slot="prefix" class="el-icon-user"></i>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.verificationCodeLabel')" prop="verificationCode">
                  <el-input
                    v-model="form.verificationCode"
                    :placeholder="$t('register.verificationCodePlaceholder')"
                    maxlength="6"
                    @input="handleVerificationCodeInput"
                  >
                    <i slot="prefix" class="el-icon-key"></i>
                    <el-button
                      slot="append"
                      type="primary"
                      :loading="codeLoading"
                      :disabled="codeButtonDisabled"
                      @click="sendVerificationCode"
                    >
                      {{ codeButtonText }}
                    </el-button>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                 <el-form-item :label="$t('register.adminPhoneLabel')" prop="administrator.phoneNumber">
                  <el-input v-model="form.administrator.phoneNumber" :placeholder="$t('register.adminPhonePlaceholder')">
                    <el-select v-model="adminPhoneCountryCode" slot="prepend" style="width: 85px">
                      <el-option label="+86" value="86"></el-option>
                      <el-option label="+49" value="49"></el-option>
                      <el-option label="+1" value="1"></el-option>
                    </el-select>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.passwordLabel')" prop="administrator.password">
                  <el-input type="password" v-model="form.administrator.password" :placeholder="$t('register.passwordPlaceholder')" show-password/>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.confirmPasswordLabel')" prop="administrator.confirmPassword">
                  <el-input type="password" v-model="form.administrator.confirmPassword" :placeholder="$t('register.confirmPasswordPlaceholder')" show-password/>
                </el-form-item>
              </el-col>
              <el-col :xs="24">
                <el-form-item :label="$t('register.adminAddressLine1Label')" prop="administrator.address.addressLine1">
                  <el-input v-model="form.administrator.address.addressLine1" :placeholder="$t('register.adminAddressLine1Placeholder')"/>
                </el-form-item>
              </el-col>
              <el-col :xs="24">
                <el-form-item :label="$t('register.adminAddressLine2Label')" prop="administrator.address.addressLine2">
                  <el-input v-model="form.administrator.address.addressLine2" :placeholder="$t('register.adminAddressLine2Placeholder')"/>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.adminCountryLabel')" prop="administrator.address.countryOrRegion">
                  <el-select v-model="form.administrator.address.countryOrRegion" filterable :placeholder="$t('register.adminCountryPlaceholder')" style="width: 100%">
                    <el-option v-for="item in countryOptions" :key="item.code" :label="item[$i18n.locale] || item.en" :value="item.code"/>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.adminStateLabel')" prop="administrator.address.stateOrProvince">
                  <el-input v-model="form.administrator.address.stateOrProvince" :placeholder="$t('register.adminStatePlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.adminCityLabel')" prop="administrator.address.city">
                  <el-input v-model="form.administrator.address.city" :placeholder="$t('register.adminCityPlaceholder')" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item :label="$t('register.adminPostalCodeLabel')" prop="administrator.address.postalCode">
                  <el-input v-model="form.administrator.address.postalCode" :placeholder="$t('register.adminPostalCodePlaceholder')" />
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 协议与提交 -->
          <div class="submit-section">
            <el-form-item prop="agreed" class="agreement-item">
              <el-checkbox v-model="form.agreed">
                {{ $t('register.agreementCheckbox') }}
              </el-checkbox>
            </el-form-item>

            <div class="terms-links">
              <button class="terms-link-btn" type="button" @click="showTermsDialog = true">
                <i class="el-icon-document"></i>
                {{ $t("register.viewTerms") }}
              </button>
              <span class="terms-divider">|</span>
              <button class="terms-link-btn" type="button" @click="showTermsDialog = true">
                <i class="el-icon-lock"></i>
                {{ $t("register.viewPrivacy") }}
              </button>
            </div>

            <el-button type="primary" @click="submitForm" class="submit-btn" :loading="loading">
              {{ $t('register.submitButton') }}
            </el-button>
            <el-button @click="resetForm" class="reset-btn">{{ $t('register.resetButton') }}</el-button>
          </div>
        </el-form>

        <!-- 服务条款与隐私政策 - 统一弹窗 -->
        <el-dialog
          :title="$t('register.termsOfService') + ' / ' + $t('register.privacyPolicy')"
          :visible.sync="showTermsDialog"
          width="80%"
          top="0"
          :append-to-body="true"
          :modal-append-to-body="true"
          custom-class="terms-dialog-wrapper"
        >
          <div class="terms-content" ref="termsContent">
            <section class="terms-block">
              <h4 class="terms-block-title">{{ $t("register.termsOfService") }}</h4>
              <template v-if="Array.isArray(termsContentValue)">
                <div
                  v-for="(section, i) in parsedSections"
                  :key="`terms-${i}`"
                  class="terms-section"
                >
                  <h5 v-if="section.title" class="terms-title">{{ section.title }}</h5>
                  <p
                    v-for="(paragraph, pIndex) in section.paragraphs"
                    :key="`terms-p-${pIndex}`"
                    class="terms-p"
                  >
                    {{ paragraph }}
                  </p>
                  <ul v-if="section.list && section.list.length" class="terms-list">
                    <li
                      v-for="(item, j) in section.list"
                      :key="j"
                      class="terms-list-item"
                    >
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </template>
              <p v-else class="terms-p">{{ termsContentValue }}</p>
            </section>

            <section class="terms-block terms-block--divider">
              <h4 class="terms-block-title">{{ $t("register.privacyPolicy") }}</h4>
              <template v-if="Array.isArray(privacyContentValue)">
                <div
                  v-for="(section, i) in parsedPrivacySections"
                  :key="`privacy-${i}`"
                  class="terms-section"
                >
                  <h5 v-if="section.title" class="terms-title">{{ section.title }}</h5>
                  <p
                    v-for="(paragraph, pIndex) in section.paragraphs"
                    :key="`privacy-p-${pIndex}`"
                    class="terms-p"
                  >
                    {{ paragraph }}
                  </p>
                  <ul v-if="section.list && section.list.length" class="terms-list">
                    <li
                      v-for="(item, j) in section.list"
                      :key="j"
                      class="terms-list-item"
                    >
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </template>
              <p v-else class="terms-p">{{ privacyContentValue }}</p>
            </section>
          </div>
          <span slot="footer">
            <el-button @click="showTermsDialog = false">{{ $t("register.termsClose") || $t("register.termsCloseButton") }}</el-button>
          </span>
        </el-dialog>
      </div>
    </div>

    <div
      v-if="popupVisible"
      ref="registrationTypePopup"
      class="description-popup"
      :style="popupStyle"
      @click.stop
      @wheel="handlePopupWheel"
    >
      <img v-if="popupImage" :src="popupImage" class="popup-image" alt="example" @load="repositionPopup" />
      <div v-if="popupParagraphs.length" class="popup-body">
        <p v-for="(paragraph, index) in popupParagraphs" :key="index" class="popup-paragraph">{{ paragraph }}</p>
      </div>
      <div v-if="popupExample" class="popup-example">
        <span class="popup-example__label">{{ $t('register.registrationNumberExample') }}</span>
        <code class="popup-example__value">{{ popupExample }}</code>
      </div>
    </div>
  </div>
</template>

<script>
import { iamApi } from '@/api/iam'
import { accountApi } from '@/api/account'
import { countryOptions } from '@/utils/countries'

export default {
  data() {
    return {
      loading: false,
      showTermsDialog: false,
      phoneCountryCode: '1',
      adminPhoneCountryCode: '1',
      windowWidth: window.innerWidth,
      codeLoading: false,
      countdown: 0,
      countdownTimer: null,
      registrationTypes: [],
      registrationTypesLoading: false,
      popupVisible: false,
      popupParagraphs: [],
      popupImage: '',
      popupExample: '',
      popupStyle: { top: '0px', left: '0px', visibility: 'hidden' },
      activePopupKey: '',
      form: {
        verificationCode: '',
        organization: {
          name: '',
          legalName: '',
          industries: [],
          address: {
            countryOrRegion: '',
            stateOrProvince: '',
            city: '',
            postalCode: '',
            addressLine1: '',
            addressLine2: '',
          },
          contactEmail: '',
          phoneNumber: '',
          legalRepresentative: {
            firstName: '',
            lastName: ''
          },
          registration: {
            registrationType: '',
            registrationNumber: '',
            registrationDocument: 'test'
          },
          taxIdentificationNumber: '',
        },
        administrator: {
          email: '',
          password: '',
          confirmPassword: '',
          sex: '',
          birthday: '',
          fullName: {
            firstName: '',
            lastName: '',
          },
          address: {
            countryOrRegion: '',
            stateOrProvince: '',
            city: '',
            postalCode: '',
            addressLine1: '',
            addressLine2: ''
          },
          nationality: '',
          phoneNumber: '',
        },
        agreed: false
      },
      countryOptions
    };
  },
  computed: {
    industryOptions() {
      return [
        { value: 'PLATFORMS', label: this.$t('register.industryOptions.platforms') },
        { value: 'ADVERTISING', label: this.$t('register.industryOptions.advertising') },
        { value: 'ENERGY', label: this.$t('register.industryOptions.energy') },
        { value: 'DATA_AND_ANALYTICS', label: this.$t('register.industryOptions.data_and_analytics') },
        { value: 'MESSAGING_AND_TELECOMMUNICATIONS', label: this.$t('register.industryOptions.messaging_and_telecommunications') },
        { value: 'LENDING_AND_INVESTMENTS', label: this.$t('register.industryOptions.lending_and_investments') },
        { value: 'REAL_ESTATE', label: this.$t('register.industryOptions.real_estate') },
        { value: 'CONSUMER_ELECTRONICS', label: this.$t('register.industryOptions.consumer_electronics') },
        { value: 'ADMINISTRATIVE_SERVICES', label: this.$t('register.industryOptions.administrative_services') },
        { value: 'HEALTH_CARE', label: this.$t('register.industryOptions.health_care') },
        { value: 'AGRICULTURE_AND_FARMING', label: this.$t('register.industryOptions.agriculture_and_farming') },
        { value: 'COMMUNITY_AND_LIFESTYLE', label: this.$t('register.industryOptions.community_and_lifestyle') },
        { value: 'SOCIAL_IMPACT', label: this.$t('register.industryOptions.social_impact') },
        { value: 'EVENTS', label: this.$t('register.industryOptions.events') },
        { value: 'CLOTHING_AND_APPAREL', label: this.$t('register.industryOptions.clothing_and_apparel') },
        { value: 'VIDEO', label: this.$t('register.industryOptions.video') },
        { value: 'INTERNET_SERVICES', label: this.$t('register.industryOptions.internet_services') },
        { value: 'TRAVEL_AND_TOURISM', label: this.$t('register.industryOptions.travel_and_tourism') },
        { value: 'INFORMATION_TECHNOLOGY', label: this.$t('register.industryOptions.information_technology') },
        { value: 'CONTENT_AND_PUBLISHING', label: this.$t('register.industryOptions.content_and_publishing') },
        { value: 'MOBILE', label: this.$t('register.industryOptions.mobile') },
        { value: 'GOVERNMENT_AND_MILITARY', label: this.$t('register.industryOptions.government_and_military') },
        { value: 'ARTIFICIAL_INTELLIGENCE', label: this.$t('register.industryOptions.artificial_intelligence') },
        { value: 'BLOCKCHAIN_AND_CRYPTOCURRENCY', label: this.$t('register.industryOptions.blockchain_and_cryptocurrency') },
        { value: 'SUSTAINABILITY', label: this.$t('register.industryOptions.sustainability') },
        { value: 'HARDWARE', label: this.$t('register.industryOptions.hardware') },
        { value: 'SALES_AND_MARKETING', label: this.$t('register.industryOptions.sales_and_marketing') },
        { value: 'TRANSPORTATION', label: this.$t('register.industryOptions.transportation') },
        { value: 'PAYMENTS', label: this.$t('register.industryOptions.payments') },
        { value: 'GAMING', label: this.$t('register.industryOptions.gaming') },
        { value: 'SCIENCE_AND_ENGINEERING', label: this.$t('register.industryOptions.science_and_engineering') },
        { value: 'APPS', label: this.$t('register.industryOptions.apps') },
        { value: 'BIOTECHNOLOGY', label: this.$t('register.industryOptions.biotechnology') },
        { value: 'PROFESSIONAL_SERVICES', label: this.$t('register.industryOptions.professional_services') },
        { value: 'NAVIGATION_AND_MAPPING', label: this.$t('register.industryOptions.navigation_and_mapping') },
        { value: 'DESIGN', label: this.$t('register.industryOptions.design') },
        { value: 'SPORTS', label: this.$t('register.industryOptions.sports') },
        { value: 'EDUCATION', label: this.$t('register.industryOptions.education') },
        { value: 'NATURAL_RESOURCES', label: this.$t('register.industryOptions.natural_resources') },
        { value: 'COMMERCE_AND_SHOPPING', label: this.$t('register.industryOptions.commerce_and_shopping') },
        { value: 'MANUFACTURING', label: this.$t('register.industryOptions.manufacturing') },
        { value: 'MEDIA_AND_ENTERTAINMENT', label: this.$t('register.industryOptions.media_and_entertainment') },
        { value: 'SOFTWARE', label: this.$t('register.industryOptions.software') },
        { value: 'CONSUMER_GOODS', label: this.$t('register.industryOptions.consumer_goods') },
        { value: 'OTHER', label: this.$t('register.industryOptions.other') },
        { value: 'MUSIC_AND_AUDIO', label: this.$t('register.industryOptions.music_and_audio') },
        { value: 'PRIVACY_AND_SECURITY', label: this.$t('register.industryOptions.privacy_and_security') },
        { value: 'FINANCIAL_SERVICES', label: this.$t('register.industryOptions.financial_services') },
        { value: 'FOOD_AND_BEVERAGE', label: this.$t('register.industryOptions.food_and_beverage') },
      ]
    },
    registrationTypeOptions() {
      return this.registrationTypes.map(type => ({
        ...type,
        value: type.code,
        label: type.name
      }))
    },
    // 注册号字段的标签跟随所选注册类型，取 registrationNumberType.name；未选择时回落到通用文案
    registrationNumberLabel() {
      const selected = this.registrationTypes.find(type => type.code === this.form.organization.registration.registrationType);
      return (selected && selected.registrationNumberType && selected.registrationNumberType.name) || this.$t('register.regNumLabel');
    },
    registrationNumberDescription() {
      const selected = this.registrationTypes.find(type => type.code === this.form.organization.registration.registrationType);
      return (selected && selected.registrationNumberType && selected.registrationNumberType.description) || '';
    },
    registrationNumberExample() {
      const selected = this.registrationTypes.find(type => type.code === this.form.organization.registration.registrationType);
      return (selected && selected.registrationNumberType && selected.registrationNumberType.example) || '';
    },
    rules() {
      const validateConfirmPassword = (rule, value, callback) => {
        if (value !== this.form.administrator.password) {
          callback(new Error(this.$t('register.validation.confirmPasswordMismatch')));
        } else {
          callback();
        }
      };
      
      const validatePassword = (rule, value, callback) => {
        if (!value) {
          return callback(new Error(this.$t('register.validation.passwordRequired')));
        }
        if (!/(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/.test(value)) {
          callback(new Error(this.$t('register.validation.passwordInvalid')));
        } else {
          callback();
        }
      };
      
      return {
        'organization.name': [{ required: true, message: this.$t('register.validation.orgNameRequired'), trigger: 'blur' }],
        'organization.legalName': [{ required: true, message: this.$t('register.validation.legalNameRequired'), trigger: 'blur' }],
        'organization.contactEmail': [{ required: true, message: this.$t('register.validation.contactEmailRequired'), trigger: 'blur' }, { type: 'email', message: this.$t('register.validation.emailInvalid'), trigger: ['blur', 'change'] }],
        'organization.phoneNumber': [{ required: true, message: this.$t('register.validation.phoneRequired'), trigger: 'blur' }],
        'organization.registration.registrationType': [{ required: true, message: this.$t('register.validation.regTypeRequired'), trigger: 'change' }],
        'organization.registration.registrationNumber': [{ required: true, message: this.$t('register.validation.regNumRequired'), trigger: 'blur' }],
        'organization.taxIdentificationNumber': [{ required: true, message: this.$t('register.validation.taxIdRequired'), trigger: 'blur' }],
        'organization.legalRepresentative.lastName': [{ required: true, message: this.$t('register.validation.legalRepLastNameRequired'), trigger: 'blur' }],
        'organization.legalRepresentative.firstName': [{ required: true, message: this.$t('register.validation.legalRepFirstNameRequired'), trigger: 'blur' }],
        'organization.address.addressLine1': [{ required: true, message: this.$t('register.validation.addressRequired'), trigger: 'blur' }],
        'organization.address.stateOrProvince': [{ required: true, message: this.$t('register.validation.stateRequired'), trigger: 'blur' }],
        'organization.address.city': [{ required: true, message: this.$t('register.validation.cityRequired'), trigger: 'blur' }],
        'organization.address.postalCode': [{ required: true, message: this.$t('register.validation.postalCodeRequired'), trigger: 'blur' }],
        'organization.address.countryOrRegion': [{ required: true, message: this.$t('register.validation.countryRequired'), trigger: 'change' }],
        'administrator.email': [{ required: true, message: this.$t('register.validation.adminEmailRequired'), trigger: 'blur' }, { type: 'email', message: this.$t('register.validation.emailInvalid'), trigger: ['blur', 'change'] }],
        'verificationCode': [
          { required: true, message: this.$t('register.validation.verificationCodeRequired'), trigger: 'blur' },
          { pattern: /^\d{6}$/, message: this.$t('register.validation.verificationCodeInvalid'), trigger: ['blur', 'change'] }
        ],
        'administrator.password': [{ required: true, validator: validatePassword, trigger: 'blur' }],
        'administrator.confirmPassword': [{ required: true, validator: validateConfirmPassword, trigger: 'blur' }],
        'administrator.fullName.firstName': [{ required: true, message: this.$t('register.validation.firstNameRequired'), trigger: 'blur' }],
        'administrator.fullName.lastName': [{ required: true, message: this.$t('register.validation.lastNameRequired'), trigger: 'blur' }],
        'agreed': [{ required: true, message: this.$t('register.validation.agreementRequired'), trigger: 'change', validator: (rule, value, cb) => value ? cb() : cb(new Error()) }]
      };
    },
    termsContentValue() {
      return this.$t('legal.termsContent');
    },
    privacyContentValue() {
      return this.$t('legal.privacyContent');
    },
    parsedSections() {
      return this.normalizeLegalSections(this.termsContentValue);
    },
    parsedPrivacySections() {
      return this.normalizeLegalSections(this.privacyContentValue);
    },
    codeButtonDisabled() {
      return this.codeLoading || this.countdown > 0;
    },
    codeButtonText() {
      if (this.countdown > 0) {
        return this.$t('register.codeCountdown', { seconds: this.countdown });
      }
      return this.$t('register.sendCodeButton');
    },
  },
  created() {
    this.loadRegistrationTypes();
  },
  watch: {
    '$i18n.locale'() {
      this.popupVisible = false;
      this.activePopupKey = '';
      this.loadRegistrationTypes();
    }
  },
  methods: {
    toggleRegistrationTypeInfo(type, event) {
      const triggerEl = event.currentTarget;
      this.popupTriggerEl = triggerEl;
      if (this.popupVisible && this.activePopupKey === type.code) {
        this.popupVisible = false;
        this.activePopupKey = '';
        return;
      }
      this.popupParagraphs = [type.description, type.registrationDocumentType?.description].filter(Boolean);
      this.popupImage = type.registrationDocumentType?.example || '';
      this.popupExample = '';
      this.popupStyle = { top: '0px', left: '0px', visibility: 'hidden' };
      this.activePopupKey = type.code;
      this.popupVisible = true;
      this.$nextTick(() => {
        this.calculatePopupPosition(triggerEl);
      });
    },
    toggleRegistrationNumberInfo(event) {
      const triggerEl = event.currentTarget;
      this.popupTriggerEl = triggerEl;
      if (this.popupVisible && this.activePopupKey === 'registrationNumber') {
        this.popupVisible = false;
        this.activePopupKey = '';
        return;
      }
      // 与账号信息页（Control Center）一致：注册号字段弹窗展示 registrationNumberType.description
      this.popupParagraphs = [this.registrationNumberDescription];
      this.popupImage = '';
      this.popupExample = this.registrationNumberExample;
      this.popupStyle = { top: '0px', left: '0px', visibility: 'hidden' };
      this.activePopupKey = 'registrationNumber';
      this.popupVisible = true;
      this.$nextTick(() => {
        this.calculatePopupPosition(triggerEl);
      });
    },
    repositionPopup() {
      if (!this.popupVisible || !this.popupTriggerEl) return;
      this.calculatePopupPosition(this.popupTriggerEl);
    },
    calculatePopupPosition(triggerEl) {
      const popupEl = this.$refs.registrationTypePopup;
      if (!popupEl || !triggerEl) return;

      const triggerRect = triggerEl.getBoundingClientRect();
      const popupRect = popupEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 8;
      const margin = 16;

      // 优先向上展开：上方空间足够；否则下方够用时向下；两侧都放不下时选空间较大的一侧并贴合视口
      const spaceAbove = triggerRect.top - margin;
      const spaceBelow = viewportHeight - triggerRect.bottom - margin;
      const required = popupRect.height + gap;
      let top;
      if (required <= spaceAbove) {
        top = triggerRect.top - popupRect.height - gap;
      } else if (required <= spaceBelow) {
        top = triggerRect.bottom + gap;
      } else {
        top = spaceAbove >= spaceBelow ? triggerRect.top - popupRect.height - gap : triggerRect.bottom + gap;
      }

      let left = triggerRect.left;
      if (left + popupRect.width > viewportWidth - margin) {
        left = viewportWidth - popupRect.width - margin;
      }
      if (left < margin) {
        left = margin;
      }

      top = Math.max(margin, Math.min(top, viewportHeight - popupRect.height - margin));

      this.popupStyle = {
        top: top + 'px',
        left: left + 'px',
        visibility: 'visible',
        transform: 'none'
      };
    },
    handleOutsideClick() {
      this.popupVisible = false;
      this.activePopupKey = '';
    },
    handleScroll(event) {
      const target = event.target;
      // 下拉面板（el-select-dropdown 挂在 body 下）内部滚动不处理
      if (target && target.closest && target.closest('.el-select-dropdown')) return;
      const popupEl = this.$refs.registrationTypePopup;
      if (this.popupVisible) {
        // 弹窗自身内容滚动（阅读长文案）时不关闭
        if (popupEl && (target === popupEl || popupEl.contains(target))) return;
        this.handleOutsideClick();
      }
      // Registration Type 下拉框展开时，滚动页面自动收起（el-select 的展开状态是 visible）
      const select = this.$refs.registrationTypeSelect;
      if (select && select.visible) {
        select.handleClose();
      }
    },
    // 滚轮只作用于弹窗：无内容可滚时直接阻断；有滚动但已到顶/到底时阻断链式滚动，避免带动页面
    handlePopupWheel(event) {
      const popupEl = this.$refs.registrationTypePopup;
      if (!popupEl) return;
      const canScroll = popupEl.scrollHeight > popupEl.clientHeight;
      if (!canScroll) {
        event.preventDefault();
        return;
      }
      const atTop = popupEl.scrollTop <= 0;
      const atBottom = popupEl.scrollTop + popupEl.clientHeight >= popupEl.scrollHeight;
      if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) {
        event.preventDefault();
      }
    },
    loadRegistrationTypes() {
      this.registrationTypesLoading = true;
      accountApi.getOrganizationRegistrationTypes()
        .then(response => {
          this.registrationTypes = response.data || response;
        })
        .catch(error => {
          console.error('获取注册类型失败:', error);
          this.registrationTypes = [];
        })
        .finally(() => {
          this.registrationTypesLoading = false;
        });
    },
    normalizeLegalSections(items) {
      if (!Array.isArray(items)) return [];
      return items.map(item => {
        if (!item) return { title: '', paragraphs: [], list: [] };
        if (typeof item === 'object') {
          return {
            title: item.title || '',
            paragraphs: Array.isArray(item.paragraphs)
              ? item.paragraphs
              : (item.text ? [item.text] : []),
            list: Array.isArray(item.list) ? item.list : []
          };
        }
        const parts = String(item).split(/\n{2,}/).map(part => part.trim()).filter(Boolean);
        const [title = '', ...paragraphs] = parts;
        return { title, paragraphs, list: [] };
      });
    },
    handleVerificationCodeInput(value) {
      this.form.verificationCode = String(value || '').replace(/\D/g, '').slice(0, 6);
    },
    sendVerificationCode() {
      this.$refs.registerForm.validateField('administrator.email', async (errorMessage) => {
        if (errorMessage) return;

        this.codeLoading = true;
        try {
          await iamApi.sendAccountCreationCode({
            target: this.form.administrator.email,
            channel: 'EMAIL'
          });
          this.$message.success(this.$t('register.codeSent'));
          this.startCountdown();
        } catch (error) {
          console.error('发送账号创建验证码失败:', error);
          this.$message.error(error.response?.data?.message || this.$t('register.codeSendFailed'));
        } finally {
          this.codeLoading = false;
        }
      });
    },
    startCountdown() {
      this.clearCountdown();
      this.countdown = 60;
      this.countdownTimer = window.setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          this.clearCountdown();
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      if (this.countdown < 0) {
        this.countdown = 0;
      }
    },
    async submitForm() {
      this.$refs.registerForm.validate(async (valid) => {
        if (valid) {
          try {
            this.loading = true;
            
            const accountData = {
              type: "ORGANIZATION",
              verificationCode: this.form.verificationCode,
              organization: {
                name: this.form.organization.name,
                legalName: this.form.organization.legalName,
                industries: this.form.organization.industries,
                address: {
                  countryOrRegion: this.form.organization.address.countryOrRegion,
                  stateOrProvince: this.form.organization.address.stateOrProvince || "",
                  city: this.form.organization.address.city,
                  postalCode: this.form.organization.address.postalCode,
                  addressLine1: this.form.organization.address.addressLine1,
                  addressLine2: this.form.organization.address.addressLine2 || ""
                },
                contactEmail: this.form.organization.contactEmail,
                phoneNumber: `+${this.phoneCountryCode}${this.form.organization.phoneNumber}`,
                legalRepresentative: {
                  firstName: this.form.organization.legalRepresentative?.firstName || "",
                  lastName: this.form.organization.legalRepresentative?.lastName || ""
                },
                taxIdentificationNumber: this.form.organization.taxIdentificationNumber,
                registration: {
                  registrationType: this.form.organization.registration.registrationType || 'OTHER',
                  registrationNumber: this.form.organization.registration.registrationNumber,
                  registrationDocument: this.form.organization.registration.registrationDocument || ""
                }
              },
              administrator: {
                email: this.form.administrator.email,
                password: this.form.administrator.password,
                fullName: {
                  firstName: this.form.administrator.fullName.firstName,
                  lastName: this.form.administrator.fullName.lastName
                },
                sex: this.form.administrator.sex || "UNKNOWN",
                birthday: this.form.administrator.birthday || "",
                phoneNumber: `+${this.adminPhoneCountryCode}${this.form.administrator.phoneNumber}`,
                address: this.form.administrator.address || {
                  countryOrRegion: "",
                  stateOrProvince: "",
                  city: "",
                  postalCode: "",
                  addressLine1: "",
                  addressLine2: ""
                },
                nationality: this.form.administrator.nationality || "CHINESE"
              }
            };

            await iamApi.register(accountData);

            this.$message.success(this.$t('register.accountCreateSuccess'));
            setTimeout(() => {
              this.$router.push('/login');
            }, 1500);

          } catch (error) {
            console.error('创建账号失败:', error);
            this.$message.error(error.response?.data?.message || this.$t('register.accountCreateFail'));
          } finally {
            this.loading = false;
          }
        } else {
          this.$message.error(this.$t('register.formError'));
          return false;
        }
      });
    },
    resetForm() {
      this.$refs.registerForm.resetFields();
    },
    goBack() {
      this.$router.go(-1);
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
      this.repositionPopup();
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('scroll', this.handleScroll, true);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('scroll', this.handleScroll, true);
    this.clearCountdown();
  }
};
</script>

<style lang="scss" scoped>
.register-page {
  min-height: 100vh;
  background-color: #f4f7fc;
  padding: 70px 2rem 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.register-container {
  width: 100%;
  max-width: 900px;
  
  @media (min-width: 1200px) {
    max-width: 1000px;
  }
  
  @media (min-width: 1400px) {
    max-width: 1100px;
  }
}

.register-card {
  background: #fff;
  border-radius: 1.25rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  padding: 2.5rem;
  position: relative;
}

.register-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.back-button {
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #888;
  cursor: pointer;
  transition: color 0.3s ease;
  &:hover {
    color: #333;
  }
}

.register-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
  margin: 0 auto 1rem;
}

.register-title {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.register-subtitle {
  font-size: 1rem;
  color: #666;
  margin-top: 0.5rem;
}

.form-section {
  margin-bottom: 2.5rem;
  &:last-of-type {
    margin-bottom: 0;
  }
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #4A5568;
  padding-bottom: 0.75rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #E2E8F0;
}

.register-form {
  ::v-deep .el-form-item__label {
    font-weight: 500;
    color: #4A5568;
    padding-bottom: 0.5rem;
    line-height: 1.2;
  }

  ::v-deep .el-input__inner,
  ::v-deep .el-textarea__inner {
    border-radius: 0.5rem;
    border: 1px solid #CBD5E0;
    height: 42px;
    background-color: #F7FAFC;
    transition: all 0.3s ease;
    &:focus {
      border-color: #667eea;
      background-color: #fff;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
    }
  }
  
  ::v-deep .el-textarea__inner {
    height: auto;
  }

  ::v-deep .el-select .el-input__inner {
    height: 42px;
  }
  
  ::v-deep .el-input-group__prepend {
    background-color: #edf2f7;
    border: 1px solid #cbd5e0;
    border-right: none;
    color: #4a5568;
    border-radius: 0.5rem 0 0 0.5rem;
  }

  /* Fix for main input when prepended */
  ::v-deep .el-input-group--prepend .el-input__inner {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  /* Fix for select input inside prepend */
  ::v-deep .el-input-group__prepend .el-select .el-input__inner {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    background-color: #edf2f7;
    border-color: #cbd5e0;
  }

  /* Verification code send button in append */
  ::v-deep .el-input-group__append {
    background-color: transparent;
    border: none;
    padding: 0;

    .el-button {
      height: 42px;
      min-width: 110px;
      margin: 0;
      padding: 0 16px;
      border: none;
      border-radius: 0 0.5rem 0.5rem 0;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      font-weight: 600;

      &.is-disabled {
        background: #cbd5e0;
        color: #fff;
        opacity: 1;
      }
    }
  }

  ::v-deep .el-input-group--append .el-input__inner {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.submit-section {
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #E2E8F0;
}

.agreement-item {
  margin-bottom: 1.5rem;
  .terms-link {
    font-weight: 500;
    color: #667eea;
  }
  ::v-deep .el-form-item__content {
    line-height: 1.5;
  }
}

.submit-btn,
.reset-btn {
  border-radius: 0.5rem;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
}

.submit-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  }
}

.reset-btn {
  background-color: #E2E8F0;
  color: #4A5568;
  &:hover {
    background-color: #CBD5E0;
  }
}

/* Tablet landscape and below */
@media (max-width: 992px) {
  .register-container {
    max-width: 750px;
  }
  
  .register-card {
    padding: 2rem;
  }
  
  .section-title {
    font-size: 1.15rem;
  }
}

/* Tablet portrait and below */
@media (max-width: 768px) {
  .register-page {
    padding: 1rem;
  }
  
  .register-container {
    max-width: 100%;
  }
  
  .register-card {
    padding: 1.5rem;
  }
  
  .register-title {
    font-size: 1.75rem;
  }
  
  .register-subtitle {
    font-size: 0.95rem;
  }
  
  .back-button {
    top: 1rem;
    left: 1rem;
    font-size: 1.25rem;
  }
  
  .section-title {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }
  
  .form-section {
    margin-bottom: 2rem;
  }
  
  .submit-btn,
  .reset-btn {
    width: 100%;
    margin-bottom: 0.75rem;
  }
  
  .submit-section {
    margin-top: 1.5rem;
    padding-top: 1rem;
  }
}

/* Mobile landscape */
@media (max-width: 576px) {
  .register-page {
    padding: 0.75rem;
  }
  
  .register-card {
    padding: 1.25rem;
    border-radius: 1rem;
  }
  
  .register-logo {
    width: 50px;
    height: 50px;
    margin-bottom: 0.75rem;
  }
  
  .register-title {
    font-size: 1.5rem;
  }
  
  .register-subtitle {
    font-size: 0.9rem;
    margin-top: 0.35rem;
  }
  
  .back-button {
    top: 0.75rem;
    left: 0.75rem;
    font-size: 1.15rem;
  }
  
  .section-title {
    font-size: 1rem;
    padding-bottom: 0.5rem;
    margin-bottom: 0.75rem;
  }
  
  .form-section {
    margin-bottom: 1.5rem;
  }
  
  .register-form {
    ::v-deep .el-form-item {
      margin-bottom: 18px;
    }
    
    ::v-deep .el-form-item__label {
      font-size: 0.9rem;
      padding-bottom: 0.35rem;
    }
    
    ::v-deep .el-input__inner,
    ::v-deep .el-textarea__inner {
      font-size: 0.95rem;
      height: 40px;
    }
    
    ::v-deep .el-select .el-input__inner {
      height: 40px;
    }
    
    ::v-deep .el-input-group__prepend {
      padding: 0 8px;
      font-size: 0.9rem;
    }
    
    ::v-deep .el-input-group__prepend .el-select {
      width: 75px !important;
      
      .el-input__inner {
        padding-left: 8px;
        padding-right: 8px;
      }
    }

    ::v-deep .el-input-group__append .el-button {
      height: 40px;
      min-width: 96px;
      padding: 0 12px;
      font-size: 0.9rem;
      border-radius: 0 0.5rem 0.5rem 0;
    }
  }
  
  .agreement-item {
    margin-bottom: 1.25rem;
    
    ::v-deep .el-checkbox__label {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
  
  .submit-btn,
  .reset-btn {
    padding: 11px 24px;
    font-size: 0.95rem;
  }
  
  .submit-section {
    margin-top: 1.25rem;
    padding-top: 0.75rem;
  }
}

/* Small mobile devices */
@media (max-width: 380px) {
  .register-page {
    padding: 0.5rem;
  }
  
  .register-card {
    padding: 1rem;
  }
  
  .register-logo {
    width: 45px;
    height: 45px;
  }
  
  .register-title {
    font-size: 1.35rem;
  }
  
  .register-subtitle {
    font-size: 0.85rem;
  }
  
  .section-title {
    font-size: 0.95rem;
  }
  
  .register-form {
    ::v-deep .el-form-item__label {
      font-size: 0.85rem;
    }
    
    ::v-deep .el-input__inner,
    ::v-deep .el-textarea__inner {
      font-size: 0.9rem;
      height: 38px;
    }
    
    ::v-deep .el-select .el-input__inner {
      height: 38px;
    }
    
    ::v-deep .el-input-group__prepend .el-select {
      width: 70px !important;
    }

    ::v-deep .el-input-group__append .el-button {
      height: 38px;
      min-width: 88px;
      padding: 0 10px;
      border-radius: 0 0.5rem 0.5rem 0;
    }
  }
  
  .submit-btn,
  .reset-btn {
    padding: 10px 20px;
    font-size: 0.9rem;
  }
}

/* Terms links section */
.terms-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
}

.terms-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #667eea;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: inherit;
  transition: all 0.2s;

  i {
    font-size: 14px;
  }

  &:hover {
    color: #764ba2;
    background: rgba(102, 126, 234, 0.08);
  }

  &:active {
    transform: scale(0.97);
  }
}

.terms-divider {
  color: #cbd5e1;
  user-select: none;
}

::v-deep .terms-dialog-wrapper .el-dialog__body {
  padding-top: 0;
}

::v-deep .terms-dialog-wrapper {
  margin: 0 auto !important;
  top: 50%;
  transform: translateY(-50%);
}

.terms-content {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    &:hover { background: #94a3b8; }
  }
}

.terms-block {
  margin-bottom: 32px;
}

.terms-block--divider {
  padding-top: 28px;
  border-top: 1px solid #e2e8f0;
}

.terms-block-title {
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 20px;
}

.terms-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.terms-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 14px;
  line-height: 1.4;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}

.terms-p {
  margin: 0 0 14px;
  line-height: 1.2;
  font-size: 14.5px;
  color: #475569;
  text-align: justify;
  word-break: break-word;
}

.terms-list {
  list-style: none;
  padding: 0;
  margin: 10px 0;

  .terms-list-item {
    position: relative;
    padding-left: 20px;
    margin-bottom: 10px;
    line-height: 1.7;
    font-size: 14.5px;
    color: #475569;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.5em;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #667eea;
      transform: translateY(-50%);
    }
  }
}

// 下拉面板挂载在 body 上，需使用顶层选择器
.registration-type-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 12px 0;
  font-size: 0.875rem;
  color: #909399;

  &__icon {
    font-size: 1rem;
    color: #667eea;
  }
}

.registration-type-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;

  &__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__info {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-right: -4px;
    color: #909399;
    font-size: 0.875rem;
    cursor: pointer;

    &:hover {
      color: #667eea;
    }
  }
}

.registration-number-label {
  display: inline-flex;
  align-items: center;
}

.registration-number-info {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-left: 2px;
  color: #909399;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    color: #667eea;
  }

  &.is-active {
    color: #667eea;
  }
}

.popup-example {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;

  &__label {
    color: #909399;
    font-size: 0.8125rem;
    flex-shrink: 0;
  }

  &__value {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    color: #303133;
    font-size: 0.875rem;
    letter-spacing: 0.02em;
    word-break: break-all;
  }
}

.description-popup {
  position: fixed;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  padding: 20px 24px;
  max-width: 480px;
  max-height: 60vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  z-index: 9999;
}

.popup-image {
  display: block;
  max-width: 80%;
  height: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin: 0 auto 12px;
}

.popup-body {
  font-size: 14px;
  line-height: 1.6;
  color: #0f172a;
  word-break: break-word;
}

.popup-paragraph {
  margin: 0 0 12px;

  &:last-child {
    margin-bottom: 0;
  }
}
</style>
